import { streamChatSse } from "@/zapi-frontend/shared/api/chatSse";
import { useEffect, useRef, useState } from "react";
import { XIcon } from "@phosphor-icons/react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { AiMessage, ChatShell, TypingDots, UserMessage } from "@/zapi-frontend/shared/ui/chat";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { chatPanelWidthClasses } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { MessageMarkdown } from "@/zapi-frontend/shared/ui/custom/ChatMessage/MessageMarkdown";
import { API_URL } from "@/zapi-frontend/shared/api/axios";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { useProjectsListChatStore, type TProjectsListChatMessage } from "@/zapi-frontend/stores/projectsListChatStore";
import { useTranslation } from "react-i18next";

type TProps = {
  isOverlay?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
};

const EMPTY_MESSAGES: TProjectsListChatMessage[] = [];
const MAX_REQUEST_MESSAGES = 40;
const PROJECT_LINK_RE = /\/projects\/(\d+)/g;

function mentionedProjectIds(content: string, validIds?: Set<number> | null): string[] {
  const ids = [...new Set([...content.matchAll(PROJECT_LINK_RE)].map((m) => m[1]))];
  return validIds ? ids.filter((id) => validIds.has(Number(id))) : ids;
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function fixProjectLinks(content: string, asText = false, validIds?: Set<number> | null): string {
  let out = content;
  const marker = "](/projects/";
  let idx = out.indexOf(marker);
  while (idx !== -1) {
    const closeParen = out.indexOf(")", idx + 2);
    if (closeParen === -1) break;
    const href = out.slice(idx + 2, closeParen);
    const linkedId = /^\/projects\/(\d+)$/.exec(href)?.[1];
    const isProject = linkedId !== undefined && (!validIds || validIds.has(Number(linkedId)));
    const isDraft =
      /^\/projects\/draft-[\w-]+$/.test(href) || (linkedId !== undefined && !!validIds && !validIds.has(Number(linkedId)));
    if (!isProject && !isDraft) {
      idx = out.indexOf(marker, idx + 1);
      continue;
    }
    let depth = 1;
    let start = -1;
    for (let i = idx - 1; i >= 0; i--) {
      const ch = out[i];
      if (ch === "]") depth++;
      else if (ch === "[") {
        depth--;
        if (depth === 0) {
          start = i;
          break;
        }
      }
    }
    if (start === -1) {
      idx = out.indexOf(marker, idx + 1);
      continue;
    }
    const text = out.slice(start + 1, idx).replace(/\\([[\]])/g, "$1");
    const draftText = isDraft && !/\(draft\)\s*$/i.test(text) ? `${text} (draft)` : text;
    const anchor =
      asText || isDraft ? `**${escapeHtml(isDraft ? draftText : text)}**` : `<a href="${href}">${escapeHtml(text)}</a>`;
    out = out.slice(0, start) + anchor + out.slice(closeParen + 1);
    idx = out.indexOf(marker, start + anchor.length);
  }
  return out;
}

type TSSEEvent =
  | { type: "start" }
  | { type: "meta"; validProjectIds?: number[] }
  | { type: "content"; content: string }
  | { type: "finish" };

async function streamProjectsListChat(
  messagesHistory: TProjectsListChatMessage[],
  organizationId: number | null,
  abortSignal: AbortSignal,
  onContentChunk: (accumulated: string) => void,
  onMeta?: (validIds: Set<number>) => void
): Promise<string> {
  let accumulated = "";

  await streamChatSse<TSSEEvent>({
    url: `${API_URL}/chat/projects-list/ask`,
    body: { messagesHistory, organizationId: organizationId ?? undefined },
    signal: abortSignal,
    onEvent: (event) => {
      if (event.type === "meta" && Array.isArray(event.validProjectIds)) {
        onMeta?.(new Set(event.validProjectIds));
      }
      if (event.type === "content") {
        accumulated += event.content;
        onContentChunk(accumulated);
      }
    }
  });

  return accumulated;
}

export function ChatPanel({ isOverlay = false, isOpen = false, onClose }: TProps) {
  const { t } = useTranslation(["projects"]);
  const currentOrganizationId = useOrganizationStore((s) => s.currentOrganizationId);
  const messages = useProjectsListChatStore(
    (s) => (currentOrganizationId ? s.historyByOrg[currentOrganizationId] : undefined) ?? EMPTY_MESSAGES
  );
  const setOrgMessages = useProjectsListChatStore((s) => s.setMessages);
  const [input, setInput] = useState("");
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const validIdsRef = useRef<Set<number> | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
  }, [currentOrganizationId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamingText]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = async () => {
    const text = input.trim();
    if (!text || isStreaming || !currentOrganizationId) return;
    const orgId = currentOrganizationId;

    const history: TProjectsListChatMessage[] = [...messages, { role: "user", content: text }];
    setOrgMessages(orgId, history);
    setInput("");
    setIsStreaming(true);
    setStreamingText("");

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      const answer = await streamProjectsListChat(
        history.slice(-MAX_REQUEST_MESSAGES),
        orgId,
        abort.signal,
        setStreamingText,
        (ids) => {
          validIdsRef.current = ids;
        }
      );
      setOrgMessages(orgId, [
        ...history,
        { role: "assistant", content: answer || t("projects:chat.noResponseFallback") }
      ]);
    } catch (e) {
      if (!abort.signal.aborted) {
        setOrgMessages(orgId, [
          ...history,
          { role: "assistant", content: t("projects:chat.genericErrorFallback") }
        ]);
      }
    } finally {
      setIsStreaming(false);
      setStreamingText(null);
    }
  };

  const clearChat = () => {
    abortRef.current?.abort();
    setIsStreaming(false);
    setStreamingText(null);
    validIdsRef.current = null;
    if (currentOrganizationId) setOrgMessages(currentOrganizationId, []);
  };

  const canClear = messages.length > 0 || isStreaming;

  return (
    <ChatShell
      data-testid="projects-chat-panel"
      title={t("projects:chat.title")}
      placeholder={t("projects:chat.placeholder")}
      submitLabel={t("projects:chat.send")}
      className={cn(
        chatPanelWidthClasses,
        isOverlay
          ? cn(
              "fixed inset-y-0 right-0 z-40 shadow-300 transition-transform duration-300 ease-in-out",
              isOpen ? "translate-x-0" : "pointer-events-none translate-x-full"
            )
          : "shadow-200"
      )}
      headerRight={
        isOverlay ? (
          <AppButton appearance="text" size="icon" onClick={onClose} aria-label={t("projects:chat.closeAriaLabel")}>
            <XIcon />
          </AppButton>
        ) : undefined
      }
      scrollRef={scrollRef}
      input={input}
      onInputChange={setInput}
      onSubmit={() => void send()}
      isBusy={isStreaming}
      onClear={clearChat}
      canClear={canClear}
    >
      <AiMessage className="w-full">
            <FigmaTypography variant="body-2" color="zinc900">
              {t("projects:chat.welcome")}
            </FigmaTypography>
          </AiMessage>

          {messages.map((m, i) => {
            if (m.role === "user") {
              return (
                <UserMessage key={i}>
                  <span className="whitespace-pre-wrap">{m.content}</span>
                </UserMessage>
              );
            }
            const mentioned = mentionedProjectIds(m.content, validIdsRef.current);
            return (
              <AiMessage key={i} className="w-full">
                <div
                  onClickCapture={(e) => {
                    const link = (e.target as HTMLElement).closest("a");
                    if (!link) return;
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(link.getAttribute("href") ?? "", "_blank", "noopener");
                  }}
                >
                  <MessageMarkdown content={fixProjectLinks(m.content, mentioned.length === 1, validIdsRef.current)} />
                </div>
                {mentioned.length === 1 && (
                  <AppButton
                    size="sm"
                    type="button"
                    className="mt-1 self-start !bg-innoveraZinc-900"
                    onClick={() => window.open(`/projects/${mentioned[0]}`, "_blank", "noopener")}
                  >
                    Open
                  </AppButton>
                )}
              </AiMessage>
            );
          })}

          {streamingText !== null && (
            <AiMessage className="w-full">
              {streamingText === "" ? (
                <TypingDots />
              ) : (
                <MessageMarkdown
                  content={fixProjectLinks(
                    streamingText,
                    mentionedProjectIds(streamingText, validIdsRef.current).length === 1,
                    validIdsRef.current
                  )}
                />
              )}
            </AiMessage>
          )}
    </ChatShell>
  );
}
